import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { io } from 'socket.io-client';
import { AuthService } from './../../../../../services/API/auth/auth.service';
import { AuxiliarService } from './../../../../../services/auxiliar.service';
import { ScalesService } from './../../../../../services/API/scales/scales.service';
import { StorageService } from './../../../../../services/API/storage/storage.service';

type SocketClient = ReturnType<typeof io>;

type ConversationKind = 'system' | 'angular' | 'scale' | 'user';

interface MonitorEntry {
  event: string;
  scale?: string;
  data?: Record<string, unknown>;
  ts: number;
}

interface ConversationLine {
  ts: number;
  author: string;
  text: string;
  kind: ConversationKind;
}

@Component({
  selector: 'app-socket-diagnostico',
  templateUrl: './socket-diagnostico.component.html',
  styleUrls: ['./socket-diagnostico.component.css'],
  standalone: false,
})
export class SocketDiagnosticoComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('conversationBox') conversationBox?: ElementRef<HTMLDivElement>;

  socketStatus: 'pending' | 'ok' | 'error' = 'pending';
  socketMessage = 'Comprobando…';
  scales: any[] = [];
  selectedScaleId: number | null = null;
  scalesLoadError = '';
  helloStatus: 'idle' | 'testing' | 'ok' | 'error' = 'idle';
  helloMessage = '';

  liveWeight: number | null = null;
  captureStatus: 'idle' | 'loading' | 'ok' | 'error' = 'idle';
  captureMessage = '';
  previewImageUrl: string | null = null;

  socketUrl = '';
  defaultSocketUrl = '';
  urlSavedMessage = '';

  chatNick = 'Angular';
  chatMessage = '';
  conversation: ConversationLine[] = [];

  colorBascula = 'rgb(55, 255, 0)';
  heightBascula = '50px';
  widthBascula = '25px';
  disabledColorBascula = 'rgb(0, 0, 0)';
  maxNumberLengthBascula = 10;

  private socket: SocketClient | null = null;
  private readonly maxLines = 250;
  private readonly nickKey = 'retsl_monitor_nick';
  private shouldScrollConversation = false;
  private pendingCaptureToken: string | null = null;
  private captureTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private auxiliarService: AuxiliarService,
    private scalesService: ScalesService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    if (!this.authService.userRolCheck('permiso_total')) {
      this.authService.logout();
      return;
    }
    this.socketUrl = this.auxiliarService.getSocket();
    this.defaultSocketUrl = this.auxiliarService.getDefaultSocket();
    const savedNick = localStorage.getItem(this.nickKey);
    if (savedNick) {
      this.chatNick = savedNick;
    }
    this.connectSocket();
    this.loadScales();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollConversation || !this.conversationBox) {
      return;
    }
    const el = this.conversationBox.nativeElement;
    el.scrollTop = el.scrollHeight;
    this.shouldScrollConversation = false;
  }

  ngOnDestroy(): void {
    this.clearCaptureTimeout();
    this.disconnectSocket();
  }

  saveSocketUrl(): void {
    const url = this.socketUrl.trim();
    if (!url) {
      this.urlSavedMessage = 'Indica una URL válida';
      return;
    }
    this.auxiliarService.setSocketUrl(url).subscribe({
      next: () => {
        this.urlSavedMessage = 'URL guardada. Reconectando…';
        this.refreshAll(false);
      },
      error: () => {
        this.urlSavedMessage = 'No se pudo guardar la URL';
      },
    });
  }

  resetSocketUrl(): void {
    this.auxiliarService.resetSocketUrl().subscribe({
      next: () => {
        this.socketUrl = this.auxiliarService.getSocket();
        this.urlSavedMessage = 'URL restaurada al valor por defecto';
        this.refreshAll(false);
      },
      error: () => {
        this.urlSavedMessage = 'No se pudo restaurar la URL';
      },
    });
  }

  connectSocket(): void {
    this.socketStatus = 'pending';
    this.socketMessage = 'Conectando…';
    this.socket = io(this.auxiliarService.getSocket(), {
      transports: ['polling', 'websocket'],
      reconnection: true,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      this.socketStatus = 'ok';
      this.socketMessage = `Conectado · ${this.socket?.id?.slice(0, 8) || '—'} · ${this.auxiliarService.getSocket()}`;
      this.socket?.emit('joinSocketMonitor');
      this.joinSelectedScale();
      this.appendLine({
        ts: Date.now(),
        author: 'Sistema',
        text: 'Conectado al servidor socket. Escuchando comunicaciones.',
        kind: 'system',
      });
    });

    this.socket.on('connect_error', (err) => {
      this.socketStatus = 'error';
      this.socketMessage = err.message || 'Error de conexión';
    });

    this.socket.on('disconnect', () => {
      if (this.socketStatus === 'ok') {
        this.socketMessage = 'Desconectado';
        this.appendLine({
          ts: Date.now(),
          author: 'Sistema',
          text: 'Desconectado del servidor socket.',
          kind: 'system',
        });
      }
    });

    this.socket.on('socketMonitor', (entry: MonitorEntry) => {
      this.handleMonitorEntry(entry);
      const line = this.monitorToConversation(entry);
      if (line) {
        this.appendLine(line);
      }
    });

    this.socket.on('sendPeso', (msg: { peso?: string | number; scale?: string | number; scale_id?: string | number }) => {
      this.updateLiveWeight(msg);
    });

    this.socket.on('captureImageResponse', (msg: { idToken?: string; imageName?: string; scale?: string | number; scale_id?: string | number }) => {
      this.handleCaptureResponse(msg);
    });

    this.socket.on('monitorChat', (msg: { nick?: string; text?: string; ts?: number }) => {
      const nick = (msg.nick || 'Usuario').trim();
      const text = (msg.text || '').trim();
      if (!text) {
        return;
      }
      this.appendLine({
        ts: msg.ts || Date.now(),
        author: nick,
        text,
        kind: 'user',
      });
    });
  }

  disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  loadScales(): void {
    this.scalesLoadError = '';
    this.scalesService.getScales('true').subscribe({
      next: (result: unknown) => {
        this.scales = this.normalizeScales(result);
        this.ensureSelectedScale();
      },
      error: () => {
        this.scalesService.getPublicScales().subscribe({
          next: (result: unknown) => {
            this.scales = this.normalizeScales(result);
            this.ensureSelectedScale();
          },
          error: () => {
            this.scales = [];
            this.selectedScaleId = null;
            this.scalesLoadError = 'No se pudieron cargar los puntos de conexión.';
          },
        });
      },
    });
  }

  private normalizeScales(result: unknown): any[] {
    if (!Array.isArray(result)) {
      return [];
    }
    return result
      .map((scale) => ({
        ...scale,
        id: Number(scale?.id),
        name: scale?.name != null ? String(scale.name) : '',
      }))
      .filter((scale) => !isNaN(scale.id));
  }

  private ensureSelectedScale(): void {
    if (this.scales.length === 0) {
      this.selectedScaleId = null;
      this.scalesLoadError = 'No hay puntos de conexión activos.';
      return;
    }
    const exists = this.scales.some((s) => s.id === this.selectedScaleId);
    if (!exists) {
      this.selectedScaleId = this.scales[0].id;
    }
    this.joinSelectedScale();
  }

  onScaleChange(): void {
    this.liveWeight = null;
    this.previewImageUrl = null;
    this.captureStatus = 'idle';
    this.captureMessage = '';
    this.helloStatus = 'idle';
    this.helloMessage = '';
    this.pendingCaptureToken = null;
    this.clearCaptureTimeout();
    this.joinSelectedScale();
  }

  testHelloControl(): void {
    const socket = this.socket;
    const scaleId = this.selectedScaleIdStr();
    if (!socket?.connected || !scaleId) {
      this.helloStatus = 'error';
      this.helloMessage = 'Socket no conectado o punto de conexión no seleccionado';
      return;
    }

    this.helloStatus = 'testing';
    this.helloMessage = 'Esperando respuesta de la app de control…';
    let resolved = false;

    const onResponse = (msg: { scale?: string | number; scale_id?: string | number }) => {
      const msgScale = String(msg?.scale ?? msg?.scale_id ?? '');
      if (msgScale !== scaleId) {
        return;
      }
      resolved = true;
      socket.off('HelloControlResponse', onResponse);
      this.helloStatus = 'ok';
      this.helloMessage = 'La app de control respondió correctamente';
    };

    socket.on('HelloControlResponse', onResponse);
    socket.emit('joinScale', this.scalePayload(scaleId));
    socket.emit('HelloControl', { msg: 'Hello', ...this.scalePayload(scaleId) });

    setTimeout(() => {
      if (resolved) {
        return;
      }
      socket.off('HelloControlResponse', onResponse);
      this.helloStatus = 'error';
      this.helloMessage = 'Sin respuesta (app de control apagada o punto de conexión incorrecto)';
    }, 3000);
  }

  requestPhoto(): void {
    const socket = this.socket;
    const scaleId = this.selectedScaleIdStr();
    if (!socket?.connected || !scaleId) {
      this.captureStatus = 'error';
      this.captureMessage = 'Socket no conectado o punto de conexión no seleccionado';
      return;
    }

    this.captureStatus = 'loading';
    this.captureMessage = 'Solicitando foto a la app de control…';
    this.previewImageUrl = null;
    const token = this.makeId(10);
    this.pendingCaptureToken = token;
    this.clearCaptureTimeout();

    socket.emit('joinScale', this.scalePayload(scaleId));
    socket.emit('captureImage', { idToken: token, ...this.scalePayload(scaleId) });

    this.captureTimeout = setTimeout(() => {
      if (this.pendingCaptureToken !== token) {
        return;
      }
      this.pendingCaptureToken = null;
      this.captureStatus = 'error';
      this.captureMessage = 'Sin respuesta de la app de control';
    }, 20000);
  }

  sendChat(): void {
    const text = this.chatMessage.trim();
    const socket = this.socket;
    if (!text || !socket?.connected) {
      return;
    }
    const nick = this.chatNick.trim().slice(0, 32) || 'Angular';
    localStorage.setItem(this.nickKey, nick);
    socket.emit('monitorChat', { nick, text });
    this.chatMessage = '';
  }

  canSendChat(): boolean {
    return this.chatMessage.trim().length > 0 && !!this.socket?.connected;
  }

  canRequestPhoto(): boolean {
    return !!this.socket?.connected && this.selectedScaleId != null && this.captureStatus !== 'loading';
  }

  clearConversation(): void {
    this.conversation = [];
  }

  formatTs(ts: number): string {
    return new Date(ts).toLocaleTimeString();
  }

  refreshAll(clearUrlMessage = true): void {
    this.disconnectSocket();
    this.connectSocket();
    this.loadScales();
    if (clearUrlMessage) {
      this.urlSavedMessage = '';
    }
  }

  private selectedScaleIdStr(): string {
    return this.selectedScaleId != null ? String(this.selectedScaleId) : '';
  }

  private joinSelectedScale(): void {
    const socket = this.socket;
    const scaleId = this.selectedScaleIdStr();
    if (!socket?.connected || !scaleId) {
      return;
    }
    socket.emit('joinScale', this.scalePayload(scaleId));
  }

  private scaleNameById(scaleId: string): string {
    if (!scaleId) {
      return '';
    }
    const scale = this.scales.find((s) => String(s.id) === String(scaleId));
    return scale?.name ? String(scale.name).trim() : '';
  }

  private scaleNameFromData(data: Record<string, unknown>, scaleId: string): string {
    const fromPayload = String(data['scale_name'] ?? data['scaleName'] ?? '').trim();
    if (fromPayload) {
      return fromPayload;
    }
    return this.scaleNameById(scaleId);
  }

  private scaleLabel(scaleId: string, data?: Record<string, unknown>): string {
    const name = data ? this.scaleNameFromData(data, scaleId) : this.scaleNameById(scaleId);
    if (name) {
      return name;
    }
    if (scaleId) {
      return `Punto #${scaleId}`;
    }
    return 'Punto de conexión';
  }

  private scalePayload(scaleId: string): { scale: string; scale_id: string; scale_name?: string } {
    const payload: { scale: string; scale_id: string; scale_name?: string } = {
      scale: scaleId,
      scale_id: scaleId,
    };
    const name = this.scaleNameById(scaleId);
    if (name) {
      payload.scale_name = name;
    }
    return payload;
  }

  private handleMonitorEntry(entry: MonitorEntry): void {
    if (entry.event !== 'sendPeso') {
      return;
    }
    const data = entry.data || {};
    this.updateLiveWeight({
      peso: data['peso'] as string | number | undefined,
      scale: (entry.scale || data['scale']) as string | number | undefined,
      scale_id: data['scale_id'] as string | number | undefined,
    });
  }

  private updateLiveWeight(msg: { peso?: string | number; scale?: string | number; scale_id?: string | number }): void {
    const scaleId = this.selectedScaleIdStr();
    if (!scaleId) {
      return;
    }
    const scale = String(msg?.scale ?? msg?.scale_id ?? '');
    if (scale !== scaleId) {
      return;
    }
    const peso = Number.parseFloat(String(msg?.peso ?? ''));
    if (!Number.isNaN(peso)) {
      this.liveWeight = peso;
    }
  }

  private handleCaptureResponse(msg: { idToken?: string; imageName?: string; scale?: string | number; scale_id?: string | number }): void {
    const scale = String(msg?.scale ?? msg?.scale_id ?? '');
    if (scale !== this.selectedScaleIdStr()) {
      return;
    }
    if (this.pendingCaptureToken && msg.idToken !== this.pendingCaptureToken) {
      return;
    }

    this.pendingCaptureToken = null;
    this.clearCaptureTimeout();

    if (!msg.imageName) {
      this.captureStatus = 'error';
      this.captureMessage = 'La app respondió sin nombre de imagen';
      return;
    }

    this.captureStatus = 'ok';
    this.captureMessage = `Foto recibida: ${msg.imageName}`;
    this.storageService.getImageFromApi(msg.imageName).subscribe({
      next: (result: { image?: string }) => {
        if (result?.image) {
          this.previewImageUrl = `data:image/jpeg;base64,${result.image}`;
        }
      },
      error: () => {
        this.captureMessage = `Foto subida (${msg.imageName}) pero no se pudo cargar la vista previa`;
      },
    });
  }

  private clearCaptureTimeout(): void {
    if (this.captureTimeout) {
      clearTimeout(this.captureTimeout);
      this.captureTimeout = null;
    }
  }

  private makeId(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  private appendLine(line: ConversationLine): void {
    this.conversation.push(line);
    if (this.conversation.length > this.maxLines) {
      this.conversation.shift();
    }
    this.shouldScrollConversation = true;
  }

  private monitorToConversation(entry: MonitorEntry): ConversationLine | null {
    const data = entry.data || {};
    const scale = entry.scale || String(data['scale'] ?? data['scale_id'] ?? '').trim();
    const scaleLabel = this.scaleLabel(scale, data);

    switch (entry.event) {
      case 'system':
        return {
          ts: entry.ts,
          author: 'Sistema',
          text: String(data['text'] || 'Evento del sistema'),
          kind: 'system',
        };
      case 'joinScale':
        return {
          ts: entry.ts,
          author: scaleLabel,
          text: scale ? `Conectada como ${scaleLabel}.` : 'Conectada al socket.',
          kind: 'scale',
        };
      case 'HelloControl':
        return {
          ts: entry.ts,
          author: 'Angular',
          text: scale
            ? `¿Estás conectada? (HelloControl → ${scaleLabel})`
            : '¿Estás conectada? (HelloControl)',
          kind: 'angular',
        };
      case 'HelloControlResponse':
        return {
          ts: entry.ts,
          author: scaleLabel,
          text: 'Sí, estoy conectada y operativa. (HelloControlResponse)',
          kind: 'scale',
        };
      case 'sendPeso':
        return {
          ts: entry.ts,
          author: scaleLabel,
          text: `Envío peso: ${data['peso'] ?? '?'} kg`,
          kind: 'scale',
        };
      case 'captureImage':
        return {
          ts: entry.ts,
          author: 'Angular',
          text: scale
            ? `Solicito captura de imagen en ${scaleLabel}${data['idToken'] ? ` (ref. ${data['idToken']})` : ''}.`
            : 'Solicito captura de imagen.',
          kind: 'angular',
        };
      case 'captureImageResponse':
        return {
          ts: entry.ts,
          author: scaleLabel,
          text: data['imageName']
            ? `Imagen capturada: ${data['imageName']}`
            : 'Imagen capturada y enviada.',
          kind: 'scale',
        };
      case 'disconnect':
        return {
          ts: entry.ts,
          author: 'Sistema',
          text: `Cliente desconectado (${data['sid'] || '?'})`,
          kind: 'system',
        };
      default:
        return {
          ts: entry.ts,
          author: entry.event,
          text: JSON.stringify(data),
          kind: 'system',
        };
    }
  }
}
